/**
 * Pure parsers for AI capacity figures stated in CLI / prose output.
 *
 * Only figures the text explicitly states are returned. Absent phrasing yields
 * nulls — never a guessed percent, total, remaining value, or reset time.
 * Aligns with executor-core retry/exhaust semantics without importing node code.
 */

import type { UsageMeasureUnit, UsageQuotaSignal } from './contract.js';
import { usedPercentOf } from './limits.js';

/** One capacity reading extracted from tool output. Every field is optional. */
export interface StatedCapacityReading {
  readonly measureUnit: UsageMeasureUnit;
  readonly usedValue: number | null;
  readonly remainingValue: number | null;
  readonly limitValue: number | null;
  /** ISO-8601 timestamp when a reset/retry moment was stated; otherwise null. */
  readonly resetsAt: string | null;
  /** True when the text matched a full-exhaustion phrase (quota exceeded, etc.). */
  readonly exhaustedStated: boolean;
}

const PERCENT_USED =
  /\b(\d{1,3}(?:\.\d+)?)\s*%\s*(?:of\s+(?:your\s+|the\s+)?(?:quota|allowance|limit|usage)\s+)?(?:used|consumed|spent)\b/i;
const PERCENT_REMAINING =
  /\b(\d{1,3}(?:\.\d+)?)\s*%\s*(?:of\s+(?:your\s+|the\s+)?(?:quota|allowance|limit|usage)\s+)?(?:remaining|left|available)\b/i;
const USED_PERCENT_OF =
  /\b(?:used|consumed)\s+(\d{1,3}(?:\.\d+)?)\s*%\b/i;
const REMAINING_PERCENT_OF =
  /\b(?:remaining|left|available)\s+(\d{1,3}(?:\.\d+)?)\s*%\b/i;
/** Codex /status and Grok /usage style: "Weekly usage: 67%" or "usage this week: 23%". */
const LABELED_USAGE_PERCENT =
  /\b(?:weekly\s+)?usage(?:\s+this\s+week)?\s*[:=]\s*(\d{1,3}(?:\.\d+)?)\s*%/i;
/** "5-hour limit: 42% used" / "Weekly limit: 15% used" already covered by PERCENT_USED when "used" follows. */
const LIMIT_LINE_PERCENT_USED =
  /\b(?:\d+-?hour|weekly|daily|monthly)\s+limit\s*[:=]\s*(\d{1,3}(?:\.\d+)?)\s*%\s*used\b/i;
const REQUESTS_LEFT =
  /\b(\d{1,12})\s*(?:of|\/)\s*(\d{1,12})\s*requests?\s*(?:left|remaining|available)\b/i;
const REQUESTS_USED_OF =
  /\b(\d{1,12})\s*(?:of|\/)\s*(\d{1,12})\s*requests?\s*(?:used|consumed)\b/i;

const RETRY_AFTER_HEADER = /retry[- ]after:?\s*(\d{1,8})\b/i;
const RETRY_IN =
  /(?:try again|available again|comes? back|resets?|retry|wait)\b[^\n]{0,40}?\bin\s+(\d{1,5})\s*(seconds?|secs?|minutes?|mins?|hours?|hrs?|days?|s|m|h|d)\b/i;
const RETRY_AT =
  /(?:try again|available again|comes? back|resets?|retry)\b[^\n]{0,40}?\b(?:at|on)\s+(\d{4}-\d{2}-\d{2}(?:[T ]\d{2}:\d{2}(?::\d{2})?(?:Z|[+-]\d{2}:?\d{2})?)?)/i;

/** Same bounds as brunel-node executor-core retryAfterSeconds. */
const RETRY_MIN_SECONDS = 60;
const RETRY_MAX_SECONDS = 2_592_000;

const UNIT_SECONDS: Readonly<Record<string, number>> = {
  s: 1, sec: 1, secs: 1, second: 1, seconds: 1,
  m: 60, min: 60, mins: 60, minute: 60, minutes: 60,
  h: 3600, hr: 3600, hrs: 3600, hour: 3600, hours: 3600,
  d: 86400, day: 86400, days: 86400,
};

/**
 * Phrases that mean the allowance is fully spent — not a percentage claim.
 * Kept aligned with brunel-node `looksExhausted` / EXHAUSTED_PATTERN intent.
 */
const EXHAUSTED_STATED =
  /quota (?:exceeded|exhausted|reached)|exceeded your (?:quota|usage|limit)|usage limit|weekly limit|hit your [^\n]{0,40}limit|out of credits?|insufficient (?:credit|credits|balance|quota|funds)|no credits? (?:left|remaining)|limit reached|reached your [^\n]{0,40}limit|billing (?:hard )?limit|upgrade to continue/i;

function clampPercent(value: number): number | null {
  if (!Number.isFinite(value) || value < 0 || value > 100) return null;
  return Math.round(value * 1e6) / 1e6;
}

function clampRetrySeconds(seconds: number): number | null {
  if (!Number.isFinite(seconds) || seconds <= 0) return null;
  return Math.min(RETRY_MAX_SECONDS, Math.max(RETRY_MIN_SECONDS, Math.round(seconds)));
}

/**
 * How long the tool said to wait, in whole seconds, or null when nothing
 * readable was stated. Same semantics as brunel-node `retryAfterSeconds`.
 */
export function statedRetryAfterSeconds(text: string, now: Date): number | null {
  const slice = text.slice(-4000);

  const header = RETRY_AFTER_HEADER.exec(slice);
  if (header?.[1] !== undefined) return clampRetrySeconds(Number(header[1]));

  const relative = RETRY_IN.exec(slice);
  if (relative?.[1] !== undefined && relative[2] !== undefined) {
    const unit = UNIT_SECONDS[relative[2].toLowerCase()];
    if (unit !== undefined) return clampRetrySeconds(Number(relative[1]) * unit);
  }

  const absolute = RETRY_AT.exec(slice);
  if (absolute?.[1] !== undefined) {
    const raw = absolute[1];
    const when = Date.parse(raw.includes(':') ? raw.replace(' ', 'T') : `${raw}T00:00:00Z`);
    if (!Number.isNaN(when)) return clampRetrySeconds((when - now.getTime()) / 1000);
  }
  return null;
}

/** Absolute reset moment as ISO string when the tool stated a wait or date. */
export function statedResetsAt(text: string, now: Date): string | null {
  const slice = text.slice(-4000);
  const absolute = RETRY_AT.exec(slice);
  if (absolute?.[1] !== undefined) {
    const raw = absolute[1];
    const when = Date.parse(raw.includes(':') ? raw.replace(' ', 'T') : `${raw}T00:00:00Z`);
    if (!Number.isNaN(when) && when > now.getTime()) return new Date(when).toISOString();
  }
  const seconds = statedRetryAfterSeconds(text, now);
  if (seconds === null) return null;
  return new Date(now.getTime() + seconds * 1000).toISOString();
}

function parsePercentUsed(text: string): number | null {
  const a = PERCENT_USED.exec(text);
  if (a?.[1] !== undefined) return clampPercent(Number(a[1]));
  const b = USED_PERCENT_OF.exec(text);
  if (b?.[1] !== undefined) return clampPercent(Number(b[1]));
  const c = LIMIT_LINE_PERCENT_USED.exec(text);
  if (c?.[1] !== undefined) return clampPercent(Number(c[1]));
  const d = LABELED_USAGE_PERCENT.exec(text);
  if (d?.[1] !== undefined) return clampPercent(Number(d[1]));
  return null;
}

function parsePercentRemaining(text: string): number | null {
  const a = PERCENT_REMAINING.exec(text);
  if (a?.[1] !== undefined) return clampPercent(Number(a[1]));
  const b = REMAINING_PERCENT_OF.exec(text);
  if (b?.[1] !== undefined) return clampPercent(Number(b[1]));
  return null;
}

/**
 * Extract capacity figures the CLI explicitly printed.
 * Does not invent a total from a bare token count. Does not upgrade a wait
 * into a percent. Exhaustion phrasing alone does not invent 100% used.
 */
export function parseCapacityFromCliOutput(text: string, now: Date = new Date()): StatedCapacityReading {
  const slice = text.slice(-4000);
  const exhaustedStated = EXHAUSTED_STATED.test(slice);
  const resetsAt = statedResetsAt(slice, now);

  const usedPct = parsePercentUsed(slice);
  const remainingPct = parsePercentRemaining(slice);
  if (usedPct !== null || remainingPct !== null) {
    return {
      measureUnit: 'percent',
      usedValue: usedPct,
      remainingValue: remainingPct,
      limitValue: null,
      resetsAt,
      exhaustedStated,
    };
  }

  const left = REQUESTS_LEFT.exec(slice);
  if (left?.[1] !== undefined && left[2] !== undefined) {
    const remaining = Number(left[1]);
    const limit = Number(left[2]);
    if (Number.isFinite(remaining) && Number.isFinite(limit) && remaining >= 0 && limit > 0 && remaining <= limit) {
      return {
        measureUnit: 'requests',
        usedValue: limit - remaining,
        remainingValue: remaining,
        limitValue: limit,
        resetsAt,
        exhaustedStated,
      };
    }
  }

  const usedOf = REQUESTS_USED_OF.exec(slice);
  if (usedOf?.[1] !== undefined && usedOf?.[2] !== undefined) {
    const used = Number(usedOf[1]);
    const limit = Number(usedOf[2]);
    if (Number.isFinite(used) && Number.isFinite(limit) && used >= 0 && limit > 0 && used <= limit) {
      return {
        measureUnit: 'requests',
        usedValue: used,
        remainingValue: limit - used,
        limitValue: limit,
        resetsAt,
        exhaustedStated,
      };
    }
  }

  // Wait / reset only — a signal, not a percentage.
  if (resetsAt !== null) {
    return {
      measureUnit: 'unknown',
      usedValue: null,
      remainingValue: null,
      limitValue: null,
      resetsAt,
      exhaustedStated,
    };
  }

  return {
    measureUnit: 'unknown',
    usedValue: null,
    remainingValue: null,
    limitValue: null,
    resetsAt: null,
    exhaustedStated,
  };
}

/** True when the reading carries at least one ledger-storable figure. */
export function capacityReadingHasFigure(reading: StatedCapacityReading): boolean {
  return reading.usedValue !== null
    || reading.remainingValue !== null
    || reading.limitValue !== null
    || reading.resetsAt !== null;
}

/**
 * Derive a quota signal from stated figures only.
 * Missing evidence → `unknown` (never silently `ok`).
 */
export function quotaSignalFromStatedFigures(input: {
  readonly usedValue: number | null;
  readonly remainingValue: number | null;
  readonly limitValue: number | null;
  readonly measureUnit: UsageMeasureUnit;
  readonly resetsAt?: string | null;
  readonly exhaustedStated?: boolean;
}): UsageQuotaSignal {
  const { usedValue, remainingValue, limitValue, measureUnit, exhaustedStated } = input;
  const hasFigure = usedValue !== null || remainingValue !== null || limitValue !== null;

  if (exhaustedStated === true) return 'exhausted';

  if (measureUnit === 'percent' || limitValue !== null) {
    const usedPct = usedPercentOf(usedValue, remainingValue, limitValue);
    if (usedPct !== null && usedPct >= 100) return 'exhausted';
    if (remainingValue === 0 && (usedValue !== null || limitValue !== null || measureUnit === 'percent')) {
      return 'exhausted';
    }
    if (usedPct !== null && usedPct >= 90) return 'near_limit';
    if (usedPct !== null) return 'ok';
  }

  if (remainingValue === 0 && limitValue !== null) return 'exhausted';
  if (remainingValue !== null && limitValue !== null && remainingValue > 0) return 'ok';
  if (usedValue !== null && limitValue !== null && limitValue > 0) {
    const pct = (usedValue / limitValue) * 100;
    if (pct >= 100) return 'exhausted';
    if (pct >= 90) return 'near_limit';
    return 'ok';
  }

  if (!hasFigure) return 'unknown';
  // A bare used count or a resetsAt-only reading is not proof of healthy quota.
  return 'unknown';
}
